import { z } from "zod";
import { BaseHttpClient, baseOk, type ApiOk } from "./base";
import {
  publicFeedbackFormResponseSchema,
  publicFeedbackAcquisitionResponseSchema,
  publicFeedbackSubmitResponseSchema,
  feedbackListResponseSchema,
  feedbackSummaryResponseSchema,
  type PublicFeedbackFormResponse,
  type PublicFeedbackAcquisitionInput,
  type PublicFeedbackAcquisitionResponse,
  type PublicFeedbackSubmitInput,
  type PublicFeedbackSubmitResponse,
  type FeedbackListQuery,
  type FeedbackListResponse,
  type FeedbackSummaryQuery,
  type FeedbackSummaryResponse,
} from "@openings/api-contract";

const publicFormOk = baseOk(publicFeedbackFormResponseSchema);
const publicAcquisitionOk = baseOk(publicFeedbackAcquisitionResponseSchema);
const publicSubmitOk = baseOk(publicFeedbackSubmitResponseSchema);
const listOk = baseOk(feedbackListResponseSchema);
const summaryOk = baseOk(feedbackSummaryResponseSchema);
const settingsOk = baseOk(
  z.object({ salonId: z.string(), feedbackEnabled: z.boolean() }),
);

export type PublicFeedbackFormResult = ApiOk<PublicFeedbackFormResponse>;
export type PublicFeedbackAcquisitionResult =
  ApiOk<PublicFeedbackAcquisitionResponse>;
export type PublicFeedbackSubmitResult = ApiOk<PublicFeedbackSubmitResponse>;
export type FeedbackListResult = ApiOk<FeedbackListResponse>;
export type FeedbackSummaryResult = ApiOk<FeedbackSummaryResponse>;
export type FeedbackSettingsResult = ApiOk<{
  salonId: string;
  feedbackEnabled: boolean;
}>;

export class FeedbackClient extends BaseHttpClient {
  // ── Public (subdomain feedback page) ──────────────────────────────────
  getPublicForm(appointmentId: string): Promise<PublicFeedbackFormResult> {
    return this.get<PublicFeedbackFormResult>(
      `/v1/public/feedback/${encodeURIComponent(appointmentId)}`,
      publicFormOk,
    );
  }

  submitPublic(
    appointmentId: string,
    input: PublicFeedbackSubmitInput,
  ): Promise<PublicFeedbackSubmitResult> {
    return this.post<PublicFeedbackSubmitResult>(
      `/v1/public/feedback/${encodeURIComponent(appointmentId)}`,
      input,
      publicSubmitOk,
    );
  }

  submitAcquisitionSourcePublic(
    appointmentId: string,
    input: PublicFeedbackAcquisitionInput,
  ): Promise<PublicFeedbackAcquisitionResult> {
    return this.post<PublicFeedbackAcquisitionResult>(
      `/v1/public/feedback/${encodeURIComponent(appointmentId)}/acquisition-source`,
      input,
      publicAcquisitionOk,
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────
  list(
    salonId: string,
    query: Partial<FeedbackListQuery> = {},
  ): Promise<FeedbackListResult> {
    const qs = new URLSearchParams();
    if (query.from) qs.set("from", query.from);
    if (query.to) qs.set("to", query.to);
    if (query.teamMemberId) qs.set("teamMemberId", query.teamMemberId);
    if (query.minRating != null) qs.set("minRating", String(query.minRating));
    if (query.limit != null) qs.set("limit", String(query.limit));
    if (query.offset != null) qs.set("offset", String(query.offset));
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.get<FeedbackListResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/feedback${suffix}`,
      listOk,
    );
  }

  summary(
    salonId: string,
    query: Partial<FeedbackSummaryQuery> = {},
  ): Promise<FeedbackSummaryResult> {
    const qs = new URLSearchParams();
    if (query.from) qs.set("from", query.from);
    if (query.to) qs.set("to", query.to);
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.get<FeedbackSummaryResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/feedback/summary${suffix}`,
      summaryOk,
    );
  }

  updateSettings(
    salonId: string,
    feedbackEnabled: boolean,
  ): Promise<FeedbackSettingsResult> {
    return this.patch<FeedbackSettingsResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/feedback-settings`,
      { feedbackEnabled },
      settingsOk,
    );
  }
}
